import { Box, CardMedia, CircularProgress, Typography, styled } from '@mui/material'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import RetakeTestModal from '../components/RetakeTestModal'
import ScoreDetailsTab from '../components/ScoreDetailsTab'
import RadarChartScore from '../components/RadarChartScore'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import SignalCellularAltRoundedIcon from '@mui/icons-material/SignalCellularAltRounded'
import { getAssessmentResult } from 'services/api/modules/performance/personality-test-assessment/assessment'
import { useQuery, useQueryClient } from 'react-query'
import { format } from 'date-fns'
import InformationImage from '../../../../../assets/images/pt-assessment-information.png'
import HelpIcon from '@mui/icons-material/Help'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxButton from 'components/custom/AdxButton'

const BoldText = styled('span')({
  fontWeight: 'bold',
})

const AssessmentDashboard = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { pathname } = useLocation()

  // labels for data array: ['FREEDOM (%)', 'ADVENTUROUS (%)', 'TEAMWORK (%)', 'SYSTEMATIC (%)', 'AMBITIOUS (%)']
  const [score, setScore] = useState([0, 0, 0, 0, 0])
  const [traits, setTraits] = useState()

  const { data, isLoading, isFetching } = useQuery(
    'assessmentResult',
    async () => {
      const { data } = await getAssessmentResult()
      return data
    },
    {
      onSuccess: (data) => {
        setScore([
          Math.round(data.freedom_score * 100),
          Math.round(data.adventurous_score * 100),
          Math.round(data.teamwork_score * 100),
          Math.round(data.systematic_score * 100),
          Math.round(data.ambitious_score * 100),
        ])
        setTraits(data.traits)
      },
      refetchOnWindowFocus: false,
    }
  )

  const submittedAt = Boolean(data?.submitted_at)
    ? format(new Date(data?.submitted_at), 'dd/MM/yyyy')
    : null

  const hasData = score.reduce((accumulator, currentValue) => accumulator + currentValue, 0) > 0

  const dataLoading = isLoading || isFetching

  useEffect(() => {
    queryClient.invalidateQueries('assessmentResult')
  }, [])

  // Learn more modal
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleClose = () => {
    setIsModalOpen(false)
  }

  return (
    <DashboardContainer headerTitle="Work Personality Fit">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography color={'light-gray-text.main'}>
            Discover your unique strengths and areas of growth with personalized personality test
            assessment. Explore the dynamic visual representation of your personality traits through
            the interactive graph below.{' '}
            <Typography
              sx={{
                display: 'inline-block',
                textDecoration: 'underline',
                color: 'alternate.main',
                cursor: 'pointer',
              }}
              onClick={() => setIsModalOpen(true)}
            >
              Learn more
            </Typography>
          </Typography>
        </Box>

        {!dataLoading && hasData ? (
          <Box
            height={'53px'}
            bgcolor={'white.main'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            padding={'12px 16px'}
            borderRadius={'4.5px'}
            border={'1px solid #E5E5E5'}
          >
            <Box display={'flex'} alignItems={'center'} gap={'16px'}>
              <SignalCellularAltRoundedIcon sx={{ color: 'alternate.main' }} />
              <Typography>
                Test taken on <BoldText>{submittedAt}</BoldText>
              </Typography>
            </Box>
            <RetakeTestModal />
          </Box>
        ) : !dataLoading ? (
          <Box
            bgcolor={'white.main'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            padding={'12px 16px'}
            borderRadius={'4.5px'}
          >
            <Box display={'flex'} alignItems={'center'} gap={'16px'}>
              <ErrorRoundedIcon sx={{ color: 'warning.main' }} />
              <Typography>You have not taken the Personality Test Assessment</Typography>
            </Box>

            <AdxButton
              variant={'contained'}
              onClick={() =>
                navigate(`${pathname}/questions`, {
                  state: {
                    isRetake: false,
                  },
                })
              }
              size={'small'}
              sx={{ width: '160px' }}
            >
              Take test
            </AdxButton>
          </Box>
        ) : null}
        <Box
          width={'full'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          sx={{ overflow: 'auto', height: 600 }}
        >
          {dataLoading ? (
            <CircularProgress color="alternate" />
          ) : (
            <RadarChartScore data={score} hasData={hasData} />
          )}
        </Box>
        {hasData && <ScoreDetailsTab data={score} traits={traits} />}
        <AdxFormModal
          open={isModalOpen}
          handleClose={handleClose}
          title="About Work Personality Fit"
          icon={<HelpIcon />}
          iconColor="info.main"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: '90vw',
              alignItems: 'center',
            }}
          >
            <CardMedia
              component="img"
              sx={{
                objectFit: 'contain',
                maxWidth: '300px',
              }}
              alt={'Work personlity fit information'}
              src={InformationImage}
            />
            <Typography>
              <b>Dive into self-discovery:</b> Our test isn&apos;t just about fitting into a role;
              it&apos;s about discovering your professional identity. We measure five core
              personality dimensions that matter in the workplace: <br />
              <br /> i. Freedom: Your desire for autonomy and self-direction. <br /> ii.
              Adventurous: Your willingness to take risks and embrace new experiences. <br /> iii.
              Teamwork: Your ability to collaborate and contribute to a group. <br /> iv.
              Systematic: Your approach to structure, planning, and organization. <br /> v.
              Ambitious: Your drive to achieve and set high goals. <br />
              <br /> <b>Visualize your fit:</b> Employers use your results to assess team
              compatibility and benchmark against current staff—ensuring you&apos;re considered for
              roles where you can truly thrive. <br />
              <br /> It&apos;s more than a test. It&apos;s a pathway to your ideal career.
            </Typography>
          </Box>
        </AdxFormModal>
      </Box>
    </DashboardContainer>
  )
}

export default AssessmentDashboard
